import { motion } from "framer-motion";
import { Heart, Scissors, Star, Users } from "lucide-react";

export default function ChiSiamoPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="chi-siamo-page">
      {/* Hero */}
      <section className="bg-black text-white py-16 border-b-4 border-accent-yellow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="font-heading text-6xl sm:text-7xl mb-4">
              CHI <span className="text-accent-yellow">SIAMO</span>
            </h1>
            <p className="font-body text-xl text-gray-300">
              RIKAMI non è solo un brand. È qualcosa che parla di te.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="font-mono text-sm uppercase text-accent-pink mb-4 block">
                LA NOSTRA STORIA
              </span>
              <h2 className="font-heading text-5xl mb-6">NATO DALLA PASSIONE</h2>
              <div className="space-y-4 font-body text-lg text-gray-700">
                <p>
                  RIKAMI nasce nel cuore dell'Italia, dalla passione per l'artigianato e per uno stile che unisce culture lontane.
                </p>
                <p>
                  Il nome stesso racconta la nostra visione: richiama un'estetica dalle sfumature giapponesi, essenziale e ricercata, e racchiude l'essenza del ricamo, gesto antico che trasforma un tessuto in identità.
                </p>
                <p>
                  RIKAMI non è nata in un ufficio, ma attorno a un tavolo di un bar, da un gruppo di amici con la stessa ossessione: l'abbigliamento come forma di espressione. Un'idea diventata progetto, un progetto diventato realtà.
                </p>
                <p>
                  Per noi il ricamo non è un dettaglio. È carattere. È precisione, è tempo, è cura. È il modo più autentico che conosciamo per dare profondità a uno stile e renderlo personale.
                </p>
                <p>
                  Ogni capo nasce dal desiderio di creare qualcosa che resti. Non solo un prodotto, ma un segno distintivo. Un punto cucito che racconta una storia.
                </p>
                <p>
                  RIKAMI è amicizia, visione condivisa e voglia di distinguersi. È tradizione che incontra lo streetwear contemporaneo. È l'idea che anche da una semplice conversazione possa nascere qualcosa di unico.
                </p>

              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="border-4 border-black shadow-brutal-lg overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1556905055-8f358a7a47b2?w=800"
                  alt="RIKAMI Workshop"
                  className="w-full h-96 object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-accent-pink border-4 border-black p-6 text-white">
                <p className="font-heading text-3xl">2024</p>
                <p className="font-mono text-sm">ANNO DI FONDAZIONE</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50 border-y-4 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-5xl mb-4">I NOSTRI VALORI</h2>
            <p className="font-body text-lg text-gray-600 max-w-xl mx-auto">
              Cio che guida ogni nostra scelta, ogni punto del nostro ricamo.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Scissors, title: "ARTIGIANATO", desc: "Ogni pezzo e fatto con cura e dedizione", color: "bg-accent-blue" },
              { icon: Heart, title: "PASSIONE", desc: "Amiamo cio che facciamo e si vede", color: "bg-accent-pink" },
              { icon: Star, title: "QUALITA", desc: "Solo materiali premium per i nostri capi", color: "bg-accent-yellow" },
              { icon: Users, title: "CREATIVITA", desc: "Creiamo qualcosa di unico che lascia il segno", color: "bg-accent-green" }
            ].map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-brutal"
              >
                <div className={`w-16 h-16 ${value.color} border-2 border-black flex items-center justify-center mb-4`}>
                  <value.icon size={32} className={value.color === "bg-accent-pink" ? "text-white" : "text-black"} />
                </div>
                <h3 className="font-heading text-xl mb-2">{value.title}</h3>
                <p className="font-body text-gray-600">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-5xl sm:text-6xl mb-6">UNISCITI A NOI</h2>
          <p className="font-body text-xl text-gray-300 mb-8 max-w-xl mx-auto">
            Entra a far parte della community RIKAMI.
            Segui il tuo stile, non le regole.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://instagram.com/rikamibrand"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-accent-pink text-white border-4 border-white px-8 py-4 font-heading text-xl hover:shadow-brutal hover:-translate-y-1 transition-transform"
            >
              SEGUICI SU INSTAGRAM
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
